<script lang="ts">
  import {getInstance} from '@/services/animation';
  import {onDestroy, onMount} from 'svelte';
  import {writable} from 'svelte/store';
  import editorInstance from '../../editor';
  import editorStore from '../../editor.store';
  import {ToolbarButton} from '../components/ToolbarButton';

  export let targetElement: HTMLElement;

  let styles = writable<{
    fontWeight: string;
    fontStyle: string;
    textTransform: string;
    textDecorationLine: string;
  } | null>(null);

  const unsubscribe = editorStore.subscribe(store => {
    if (store.activeComponent) {
      targetElement = store.activeComponent.querySelector('.dynamic') as HTMLElement;
      if (!targetElement) return;
    }
  });

  const loadStyles = () => {
    if (!targetElement) return;
    styles.set(window.getComputedStyle(targetElement));
  };

  const recomputeTextSizing = () => {
    return new Promise(resolve => {
      const instance = getInstance();
      if (instance) {
        instance.templateLoader.recomputeTemplateMetadata();
        instance.recomputeCaptionTextSize();
        // Wait for the next animation frame
        requestAnimationFrame(() => setTimeout(() => resolve(true), 50));
      } else {
        resolve(false);
      }
    });
  };

  const toggleStyle = async (
    property: 'fontWeight' | 'fontStyle' | 'textDecoration' | 'textTransform',
    currentValue: boolean,
    pair: [string, string],
    event: string
  ) => {
    editorInstance.history.addHistory(event);
    targetElement.style[property] = currentValue ? pair[0] : pair[1];
    loadStyles();
    await recomputeTextSizing();
    modules = modules;
  };

  const toggleWeight = () => toggleStyle('fontWeight', isBold, ['500', '700'], 'weight change');
  const toggleItalic = () => toggleStyle('fontStyle', isItalic, ['normal', 'italic'], 'font style change');
  const toggleUnderline = () => toggleStyle('textDecoration', isUnderline, ['none', 'underline'], 'underline change');
  const toggleCase = () => toggleStyle('textTransform', isUppercase, ['none', 'uppercase'], 'case change');

  $: isBold = $styles?.fontWeight === '700';
  $: isItalic = $styles?.fontStyle === 'italic';
  $: isUppercase = $styles?.textTransform === 'uppercase';
  $: isUnderline = $styles?.textDecorationLine === 'underline';

  $: modules = [
    {tooltip: 'Bold', icon: 'fa fa-bold', isActive: isBold, onClick: toggleWeight},
    {tooltip: 'Italic', icon: 'fa fa-italic', isActive: isItalic, onClick: toggleItalic},
    {tooltip: 'Underline', icon: 'fa fa-underline', isActive: isUnderline, onClick: toggleUnderline},
    {tooltip: 'Font Case', icon: 'fa-solid fa-font-case', isActive: isUppercase, onClick: toggleCase}
  ];

  $: active = modules.some(module => module.isActive);

  onMount(() => {
    loadStyles();
  });

  onDestroy(unsubscribe);
</script>

<ToolbarButton label="Text Emphasis" icon="fa fa-bold" {modules} {active} />
